import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, XCircle, Loader2, MinusCircle, Settings2 } from "lucide-react";
import { Button } from "./ui/button";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";
import { ResearchBadge } from "./Badges";

const ICON = {
  done: <CheckCircle2 className="h-4 w-4 text-green-600" />,
  running: <Loader2 className="h-4 w-4 animate-spin text-orange-600" />,
  failed: <XCircle className="h-4 w-4 text-red-600" />,
  setup_required: <Settings2 className="h-4 w-4 text-purple-600" />,
  skipped: <MinusCircle className="h-4 w-4 text-slate-400" />,
  pending: <Circle className="h-4 w-4 text-slate-300" />,
};
const ACTIVE = ["queued", "running"];

export function useJobPolling(initial, onFinish) {
  const [job, setJob] = useState(initial);
  useEffect(() => {
    setJob(initial);
  }, [initial?.id]); // eslint-disable-line
  useEffect(() => {
    if (!job || !ACTIVE.includes(job.status)) return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/jobs/${job.id}`);
        setJob(data);
        if (!ACTIVE.includes(data.status)) onFinish?.(data);
      } catch {
        /* keep polling */
      }
    }, 2500);
    return () => clearInterval(t);
  }, [job?.id, job?.status]); // eslint-disable-line
  return job;
}

export const ResearchProgress = ({ job, compact = false }) => {
  if (!job) return null;
  const cancel = async () => {
    try {
      await api.post(`/jobs/${job.id}/cancel`);
      toast.success("Cancellation requested — the job stops after the current stage");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const u = job.usage || {};
  return (
    <div className="print-card rounded-lg border bg-white p-5 shadow-sm" data-testid={`research-progress-${job.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold">{job.location_name}</div>
          <div className="mt-1"><ResearchBadge status={job.status} testId="job-status-badge" /></div>
        </div>
        <div className="flex gap-2">
          {ACTIVE.includes(job.status) && <Button size="sm" variant="outline" onClick={cancel} data-testid="job-cancel-btn">Cancel</Button>}
          {job.report_id && !ACTIVE.includes(job.status) && (
            <Button asChild size="sm" className="bg-orange-600 hover:bg-orange-700" data-testid="job-open-report-btn">
              <Link to={`/leads/${job.location_id}`}>Open report</Link>
            </Button>
          )}
        </div>
      </div>
      <ol className="mt-4 space-y-2">
        {job.stages.map((s, i) => (
          <li key={s.key} className="flex gap-3" data-testid={`stage-${s.key}`}>
            <div className="mt-0.5">{ICON[s.status] || ICON.pending}</div>
            <div className="min-w-0">
              <div className={`text-sm ${s.status === "pending" ? "text-slate-400" : "font-medium text-slate-800"}`}>{i + 1}. {s.label}</div>
              {!compact && s.message && <div className="break-words text-xs text-slate-500">{s.message}</div>}
            </div>
          </li>
        ))}
      </ol>
      <div className="mt-4 border-t pt-3 font-mono text-[11px] text-slate-500" data-testid="job-usage">
        Usage: {u.places_calls || 0} Places call · {u.pages_fetched || 0} pages retrieved · {u.pages_failed || 0} inaccessible · {u.searches_run || 0} searches · {u.pagespeed_calls || 0} PageSpeed · {u.llm_calls || 0} AI call (~{Math.round(((u.llm_input_chars || 0) + (u.llm_output_chars || 0)) / 4).toLocaleString()} tokens, estimated from characters)
      </div>
    </div>
  );
};

export const PolledProgress = ({ job: initial, onFinish, compact }) => {
  const job = useJobPolling(initial, onFinish);
  return <ResearchProgress job={job} compact={compact} />;
};
